import { useEffect, useState } from 'react';

import Spinner from '../../../../reusable/spinner/spinner.component';
import Client from '../../../../../tools/client';

import {
  Container,
  FieldGroup,
  FieldSection,
  Label,
  Input,
  Toggle,
  ButtonRow,
  Button,
  Description
} from '../../configuration-sections.styles';

const client = new Client();

const AgeVerificationConfigurationDisplay = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const fetchConfig = async () => {
      const res = await client.setup();
      const config = res.configuration?.legal?.ageVerify || {};

      setForm({
        active: config.active === 'true' || config.active === true,
        age: config.age || 21,
        title: config.title || '',
        titleActive: config.titleActive === 'true' || config.titleActive === true,
        text: config.text || '',
        textActive: config.textActive === 'true' || config.textActive === true
      });

      setLoading(false);
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleToggle = (key) => {
    setForm({ ...form, [key]: !form[key] });
  };

  const handleSave = async () => {
    const entries = {
      'legal.ageVerify.active': form.active,
      'legal.ageVerify.age': form.age,
      'legal.ageVerify.title': form.title,
      'legal.ageVerify.titleActive': form.titleActive,
      'legal.ageVerify.text': form.text,
      'legal.ageVerify.textActive': form.textActive
    };

    for (const [key, value] of Object.entries(entries)) {
      await client.updateConfigurationByKey(key, {
        value: typeof value === 'boolean' ? String(value) : value
      });
    }

    setEditing(false);
  };

  if (loading) return <Spinner />;

  return (
    <Container>
      <FieldSection>
        <FieldGroup>
          <Label htmlFor="active">Require Age Verification</Label>
          <Description>
            Enables an age verification overlay before users can access your site.
          </Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.active}
              onChange={() => handleToggle('active')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="age">Minimum Age</Label>
          <Description>
            The minimum age a visitor must confirm to enter your website.
          </Description>
          <Input
            type="number"
            name="age"
            value={form.age}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="titleActive">Show Custom Title</Label>
          <Description>
            If disabled, a default title will be displayed on the age verification modal.
          </Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.titleActive}
              onChange={() => handleToggle('titleActive')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="title">Custom Title</Label>
          <Description>
            Heading shown on the age verification prompt (e.g., "Welcome" or "Age Check").
          </Description>
          <Input
            name="title"
            value={form.title}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="textActive">Show Custom Text</Label>
          <Description>
            If disabled, a default message will be displayed under the title.
          </Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.textActive}
              onChange={() => handleToggle('textActive')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="text">Custom Text</Label>
          <Description>
            Additional message below the title on the age verification modal.
          </Description>
          <Input
            name="text"
            value={form.text}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>
      </FieldSection>

      <ButtonRow>
        {editing ? (
          <>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button primary onClick={handleSave}>Save</Button>
          </>
        ) : (
          <Button primary onClick={() => setEditing(true)}>Edit</Button>
        )}
      </ButtonRow>
    </Container>
  );
};

export default AgeVerificationConfigurationDisplay;